import sweetalert from "sweetalert";
import { _ } from "extensions/underscore";
import Radio from "backbone.radio";
import { captureException, captureMessage } from "extensions/sentry";
import { webix } from "extensions/webix";
import "vendor/pivot/sources/pivot";
import ReactDOMServer from "react-dom/server";
import { render } from "react-dom";
import classNames from "classnames";
import {
    constant,
    difference,
    divide,
    first,
    identity,
    last,
    omit,
    partial,
    pick,
    range,
    startsWith,
    sum,
    isNumber
} from "lodash-es";

import {
    amount,
    number,
    rate,
    deltaRate,
    deltaNumber,
    deltaAmount,
    rateDisabled
} from "common/formats";
import { emitter } from "common/emitter";
import { makeElFromString, nextTick } from "common/utils";
import strings from "common/strings";
import { store } from "common/store";

import { getters } from "common/components/getters/getters";
import { references } from "common/helpers/references";
import { products } from "common/routers/products";

import { getCurrencyDecimals } from "common/helpers/currency";

import { ErrorView } from "common/components/error";
import { Tooltip } from "common/components/tooltip";
import { log } from "common/components/debug";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import {
    faFlag as fasFlag,
    faPiggyBank as fasPiggyBank
} from "@fortawesome/pro-solid-svg-icons";
import {
    faFlag as farFlag,
    faPiggyBank as farPiggyBank
} from "@fortawesome/pro-regular-svg-icons";

import pivotSettingsImage from "img/pivot-table-settings.svg";

// Upgrade webix pivot popup to read extra fields from the popup config
try {
    const mixin = webix.ui.pivot.$protoWait.find((i) => i.getFields);
    const original = mixin.getFields;
    mixin.getFields = function(...args) {
        const results = original.apply(this, args);
        
        if (Array.isArray(this.config.availablePivots)) {
            this.config.availablePivots.forEach((p) => {
                if (!results.fields.some(({ name }) => name === p)) {
                    results.fields.push({
                        name: p,
                        text: this._applyMap(p),
                        id: webix.uid()
                    });
                }
            });
        }
        
        return results;
    };
} catch (error) {
    console.error(error);
    throw new Error(error);
}

webix.i18n.pivot.windowTitle = "Pivot table options";
webix.i18n.pivot.windowMessage = "";

export function processDataForPivotTable({
    data = [],
    values = [],
    rows = [],
    dates = [],
    product = null,
    baseDate = "previous"
}) {
    const baseRangeValuesMap = Object.create(null); // Protection against Prototype pollution attack
    const makeValueHash = function(value) {
        return Object.entries(value).reduce((memo, [key, val]) => {
            if (key !== "date_range" && rows.includes(key)) return [...memo, val];
            return memo;
        }, []).join("_");
    };
    
    const getNAorValue = function(value) {
        if (value === "N/A" || typeof value === "undefined") return "";
        return value;
    };
    
    return data.map((model) => {
        const remasteredModel = {
            ...model
        };
        
        // Get or set hash for the value for faster lookups to calculate "interval_change" values later
        let hashMatch;
        if (dates.length > 1) {
            const hash = makeValueHash(remasteredModel);
            // Get index of the date
            const index = dates.indexOf(model.date_range || model.date_time);
            if (baseDate === "first") {
                if (index === 0) {
                    baseRangeValuesMap[hash] = model;
                    hashMatch = void 0;
                } else {
                    hashMatch = baseRangeValuesMap[hash];
                }
            } else if (baseDate === "previous") {
                baseRangeValuesMap[hash] = baseRangeValuesMap[hash] || [];
                baseRangeValuesMap[hash][index] = model;
                hashMatch = baseRangeValuesMap[hash][Math.max(0, index - 1)];
            }
        }
        
        if (remasteredModel.currency && !rows.includes("currency_alias")) {
            remasteredModel.currency_alias = remasteredModel.currency;
        }
        
        // Add requested card fields
        values.forEach((f) => {
            if (f.key === "chargebacks_rate" && !Object.prototype.hasOwnProperty.call(model, "chargebacks_rate")) {
                remasteredModel[f.key] = 0;
            } else {
                remasteredModel[f.key] = getNAorValue(model[f.key]);
            }
            
            if (Array.isArray(f.weighted_deps)) {
                Object.assign(remasteredModel, f.weighted_deps.reduce((memo, id) => {
                    const getter = getters.findById(id);
                    return {
                        ...memo,
                        [getter.key]: getNAorValue(model[getter.key])
                    };
                }, {}));
            }
            
            // Add rank and median values to be used by benchmark tables
            if (product === products.benchmarks && f.benchmark_deps) {
                Object.values(f.benchmark_deps).forEach((id) => {
                    const getter = getters.findById(id);
                    if (getter) {
                        Object.assign(remasteredModel, {
                            [getter.key]: getNAorValue(model[getter.key])
                        });
                    } else {
                        // Capture exception, but do not error on the UI
                        captureException(new Error(`Cannot find getter in benchmark dependencies: ${id}`), {
                            extra: {
                                f
                            }
                        });
                    }
                });
            }
            
            if (dates.length > 1) {
                Object.assign(remasteredModel, {
                    // Calculate delta for the change column
                    [`internal_change_${f.key}`]: hashMatch ? (model[f.key] || 0) - (hashMatch[f.key] || 0) : void 0
                });
            }
        });
        
        // Return remastered model back to map iteratee
        return remasteredModel;
    });
}

export const events = {
    onItemClick({ checkbox, data = [], callback = () => {} } = {}) {
        return function(id) {
            if (checkbox && id.column === "ch1") return;

            // Prevent default onItem click handler
            if (id.column === "recon3actions") return;
            
            callback(getItemFiltersById({
                id,
                pivot: this
            }), id, this);
        };
    },
    onBeforeRenderPivotTable({
        fieldFormat,
        autoAdjustColumns = false,
        checkbox = false,
        recon3actions = false,
        calculated_fields = true,
        columnMapIteratee = (c) => c,
        parentWidth = 0,
        initialSorting = {}
    } = {}) {
        return function(config) {
            // Add reserve sum operations to support multiple columns of the same field
            range(2, 5).forEach((op) => {
                this.operations[`sum${op}`] = this.operations.sum;
            });

            // Add weighted_sum operation to calculate correct relative change
            // in change over time table for branch rows instead of just summing values
            this.operations.weighted_sum = (values, webixColumnName, webixRowDataset) => {
                // Check if it is a branch
                if (webixRowDataset.data && values.length > 1 && values.every((v) => isNumber(v))) {
                    // Get timeframe stamp form column name
                    const frameName = first(webixColumnName.split("_'_"));
                    const data = webixRowDataset.data
                        // Map data for current level and pick values for current column
                        .map((dataObj) => pick(dataObj, Object.keys(dataObj).filter((k) => k.includes(frameName))))
                        // Aggregate previous and current period values
                        .reduce((memo, item) => {
                            const change = item[webixColumnName] / 100;
                            const value = Number(Object.values(omit(item, webixColumnName)));
                            const prevValue = divide(value, change + 1); // calculate previous value from current and change %

                            return {
                                value: sum([memo.value, value]),
                                prevValue: sum([memo.prevValue, prevValue])
                            };
                        }, Object.create({ value: 0, prevValue: 0 }));
                    
                    // Calculate change value for level
                    return divide(data.value - data.prevValue, data.prevValue) * 100;
                }
                
                // Noop for leaf level
                return values.length ? (values.length > 1 ? values : values[0]) : [];
            };
            
            // Show active rows in the header
            const { rows, columns, values } = this.getStructure();
            if (config.header && config.header[0]) {
                const pivots = rows.map((r) => this.config.fieldMap[r]);
                if (pivots.length) {
                    // Don't show configure icon in alerts because it is a readonly pivot table
                    if (this.config.product === products.alerts) {
                        config.header[0].header = `<div class="webix_pivot_config_msg">${pivots.join(" > ")}</div>`;
                    } else {
                        config.header[0].header = ReactDOMServer.renderToStaticMarkup(
                            <div className="webix_pivot_config_msg">
                                {!this.config.readonly && recon3actions && <img src={pivotSettingsImage} alt="Configure table" className="pivot-table-icon" width="45"/>}
                                {pivots.join(" > ")}
                            </div>
                        );
                    }
                } else {
                    config.header[0].header = `<div class="webix_pivot_config_msg">${webix.i18n.pivot.pivotMessage}</div>`;
                }
            }
            
            // Format data fields
            if (!fieldFormat) {
                return;
            }
            
            // Disable sort on change columns
            config.header = config.header.map((column) => {
                if (column.id.includes("'_internal_change")) {
                    return {
                        ...column,
                        sort: false,
                        header: column.header.map((h) => ({
                            ...h,
                            css: "no-sort"
                        }))
                    };
                }
                // Add change header to change-over-time columns
                if (["pct_change_", "abs_change_"].some((substr) => column.id.includes(substr))) {
                    return {
                        ...column,
                        tooltip: false,
                        header: column.header.map((h) => {
                            if (["pct_change_", "abs_change_"].some((substr) => h.name.includes(substr))) {
                                return {
                                    ...h,
                                    tooltip: h.text,
                                    text: ReactDOMServer.renderToStaticMarkup(<span>Change&nbsp;<FontAwesomeIcon icon={faQuestionCircle} /></span>),
                                    css: "change-column-header"
                                };
                            }
                            return h;
                        })
                    };
                }

                return column;
            });

            if (autoAdjustColumns) {
                config.header = config.header.map((column) => ({ ...column, adjust: true }));
            }
            
            const formatValueForProduct = (formatter) => {
                if (this.config.product === products.benchmarks) {
                    return (value) => {
                        if (value === "") return "N/A";
                        return formatter(value);
                    };
                }
                
                return formatter;
            };
            
            // Define map with valueType => format fn
            const formats = {
                number: formatValueForProduct(number),
                delta_number: formatValueForProduct(deltaNumber),
                rate: formatValueForProduct(calculated_fields ? rate : partial(rateDisabled, _, { tooltip: true })),
                delta_rate: formatValueForProduct(calculated_fields ? deltaRate : partial(rateDisabled, _, { tooltip: true })),
                amount: formatValueForProduct(amount),
                delta_amount: formatValueForProduct(deltaAmount),
                "default": identity
            };
            
            const weightedFieldsArray = [];
            const originalCurrencyArray = [];
            const datatable = this.$$("data");
            
            fieldFormat.forEach((ff) => {
                const match = config.header.filter((column) => [
                    `'_${ff.name}`,
                    `'_internal_change_${ff.name}`,
                    `'_pct_change_${ff.name}`,
                    `_abs_change_${ff.name}`
                ].some((str) => column.id.includes(str)));
                match.forEach((m) => {
                    if (ff.valueType === "amount" && ff.original_currency) {
                        m.template = (item, type, value, c, i) => {
                            // Return earlier if value is empty string
                            if (value === "") {
                                return "";
                            }

                            let sourceID = item.id;
                            if (Array.isArray(item.$source) && item.$source.length) {
                                sourceID = item.$source[0];
                            }
                            const matchingField = this.getItem(sourceID);
                            
                            if (matchingField && ["currency_alias", "purchase_currency", "stl_currency"].some((f) => Object.prototype.hasOwnProperty.call(matchingField, f))) {
                                const currency = ff.currency_attribute
                                    ? matchingField[ff.currency_attribute]
                                    : matchingField.currency_alias || matchingField.stl_currency;
                                
                                let decimals = 2;
                                if (currency) {
                                    decimals = getCurrencyDecimals(currency);
                                }
                                
                                if (m.id.search("_internal_change_") !== -1) {
                                    return formats.delta_amount(value, { currency, decimals });
                                }
                                // if (m.id.search("pct_change") !== -1) {
                                //     return formats.rate(value);
                                // }
                                
                                return formats.amount(value, { currency, decimals });
                            }

                            // Don't calculate amounts for branch if it has multiple currencies
                            if (datatable.data.isBranch(sourceID)) {
                                const currenciesOfBranch = new Set();
                                try {
                                    datatable.data.eachLeaf(sourceID, (leaf_id) => {
                                        if (leaf_id.$source) {
                                            leaf_id.$source.forEach((obj_id) => {
                                                const obj = this.getItem(obj_id);
                                                currenciesOfBranch.add(obj?.stl_currency || obj?.[ff.currency_attribute] || obj?.currency_alias);
                                                if (currenciesOfBranch.size > 1) {
                                                    // Need to trow here to exit .eachLeaf()
                                                    throw new Error("Escape webix eachLeaf with noop");
                                                }
                                            });
                                        }
                                    });
                                    
                                    if (currenciesOfBranch.size === 1) {
                                        const currency = currenciesOfBranch.values().next().value;
                                        const decimals = getCurrencyDecimals(currency);
                                        return formats.amount(value, { currency, decimals });
                                    }
                                } catch (error) {
                                    log(error);
                                }
                            }
                            // Do not show anything if the item not in the filters list. Example:
                            // if we get three pivots table provider -> currency -> card_type then we should not sum original amounts under provider
                            return "";
                        };
                    } else if (m.id.search("_internal_change_") !== -1) {
                        m.format = formats[`delta_${ff.valueType}`] || formats[ff.valueType] || formats.default;
                    } else if (m.id.search("_pct_change_") !== -1) {
                        m.format = formats.delta_rate;
                    } else if (m.id.search("_abs_change_") !== -1) {
                        m.format = formats[`delta_${ff.valueType}`] || formats[ff.valueType] || formats.default;
                    } else {
                        m.format = formats[ff.valueType] || formats.default;
                    }

                    if (["pct_change_", "abs_change"].some((substr) => m.id.includes(substr))) {
                        m.css = "change-column";
                    }
                    
                    // Custom CSS class name for each cell. This will include valueType + any custom value-based class
                    m.cssFormat = this.config.product === products.benchmarks ? (value) => {
                        if (value.toLowerCase() === "n/a") {
                            return `not-available-value ${ff.className}`;
                        }
                        
                        return ff.className;
                    } : constant(ff.className);
                    
                    if (ff.weighted) {
                        weightedFieldsArray.push({
                            pivot_field_id: m.id,
                            field: ff
                        });
                        
                        const oldTemplateFn = m.template;
                        m.template = (obj, common, cellValue, colConfig) => {
                            // Return empty string if the object is a filler row for pie-chart view
                            if (obj.filler) {
                                return "";
                            }
                            
                            // Return empty string on multi-pivot rows in benchmarks
                            // Check if the cell in the leaf or in the branch
                            if ([products.benchmarks, products.alerts].includes(this.config.product) && datatable.data.isBranch(obj.id) && !this.getItem(obj.id)) {
                                // Return empty string if there is no source for the ID (meaning it's computed cell)
                                return "";
                            }
                            
                            let updatedCellValue = cellValue;
                            
                            let sourceData = [];
                            
                            // If the item is a branch-level item data
                            const sourceGetterIteratee = (i) => !i.filler && sourceData.push(...i.$source.map(this.getItem));
                            if (datatable.data.isBranch(obj.id)) {
                                datatable.data.eachLeaf(obj.id, sourceGetterIteratee);
                            } else {
                                sourceGetterIteratee(obj);
                            }
                            
                            // From the source data we should filter only sources applicable for the current column
                            if (columns.length) {
                                // column ID includes all required information regarding the structure:
                                // colLevel1_'_/* . . . */_'_colLevelN_'_operation_'_value
                                const currentColumnConfig = colConfig.id.split(this.$separator);
                                // Remove value column and operation, leaving only colLevel1, colLevel1, colLevelN.
                                currentColumnConfig.splice(-2, 2);
                                
                                // Now filter source data to keep only sources where each column from structure config equals to current index column config
                                sourceData = sourceData.filter((s) => columns.every((c, i) => s[c] === currentColumnConfig[i]));
                            }
                            
                            // Don't transform when there is only one source
                            if (sourceData.length !== 1) {
                                if (typeof ff.func === "function") {
                                    updatedCellValue = ff.func(sourceData, Object.values(this.data.pull));
                                } else {
                                    // Return empty string if it is weighted value but no func on how to compute it
                                    return "";
                                }
                            }
                            
                            if (typeof oldTemplateFn === "function") {
                                return oldTemplateFn(obj, common, updatedCellValue, colConfig);
                            }
                            
                            if (typeof updatedCellValue === "string") {
                                return updatedCellValue;
                            }
                            
                            return m.format(updatedCellValue);
                        };
                    }
                    
                    if (rows.includes("date_range") || rows.includes("date_time")) {
                        const oldTemplateFn = m.template;
                        
                        m.template = (obj, common, cellValue, colConfig) => {
                            // Check if the cell in the leaf or in the branch
                            if (datatable.data.isBranch(obj.id) // Return empty string if there is no source for the ID (meaning it's computed cell)
                                && !this.getItem(obj.id)) {
                                return "";
                            }
                            
                            if (typeof oldTemplateFn === "function") {
                                return oldTemplateFn(obj, common, cellValue, colConfig);
                            }
                            
                            if (typeof cellValue === "string") {
                                return cellValue;
                            }
                            
                            return m.format(cellValue);
                        };
                    }
                    
                    if (ff.original_currency) {
                        originalCurrencyArray.push({
                            pivot_field_id: m.id,
                            field: ff
                        });
                    }
                });
            });
            
            // Check if there is initialSorting defined in the model
            let { field } = initialSorting;
            const { dir } = initialSorting;
            
            if (field && dir && datatable) {
                // If provided sort field is not found then use first available value
                if (values.length && !values.some(({ name }) => name === field)) {
                    field = values[0].name;
                } else {
                    // it's a brand new custom pivot or user deleted all and this prevents error then hit apply.
                    field = "";
                }
                // Here we assume that sum is always default operation
                datatable.markSorting(`sum${this.$separator}${field}`, dir);
            }
            
            if (recon3actions) {
                this.selectedRows = {};
                config.header = [
                    {
                        id: "recon3actions",
                        header: "",
                        template(obj, common, value, c) {
                            if (typeof obj.reconciled === "undefined" || typeof obj.flagged === "undefined") {
                                Object.assign(obj, calculateFlaggedReconciled(datatable, obj));
                            }
                            return ReactDOMServer.renderToStaticMarkup(
                                <div className="flex-content">
                                    <div className="controls">
                                        <div className={classNames("control-icon", "flagged", `status-${obj.flagged}`)}>
                                            <FontAwesomeIcon icon={obj.flagged ? fasFlag : farFlag} size="lg"/>
                                        </div>
                                        <div className={classNames("control-icon", "reconciled", `status-${obj.reconciled}`)}>
                                            <FontAwesomeIcon icon={obj.reconciled ? fasPiggyBank : farPiggyBank} size="lg"/>
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                    },
                    ...config.header
                ];
            }

            // Add checkbox
            if (checkbox) {
                this.selectedRows = {};
                config.header = [
                    {
                        id: "ch1",
                        header: { content: "masterCheckbox" },
                        checkValue: "on",
                        uncheckValue: "off",
                        template(obj, common, value, c) {
                            // if (obj.$level === totalRows) {
                            //     return common.checkbox(obj, common, value, c);
                            // }
                            // return "";
                            
                            // Don't display checkbox for artificial row in chart mode
                            if (obj.filler === true) {
                                return "";
                            }
                            return common.checkbox(obj, common, value, c);
                        },
                        width: 42
                    },
                    ...config.header
                ];
            }
            
            if (rows.includes("date_range") || rows.includes("date_time")) {
                config.header[recon3actions || checkbox ? 1 : 0].cssFormat = (value, item) => {
                    if (item.$count || item.filler) {
                        return void 0;
                    }
                    
                    return "date_range_cell";
                };
            }
            
            config.header[recon3actions || checkbox ? 1 : 0].template = (obj, common, value, c, index) => {
                const key = this.config.structure.rows[obj.$level - 1];
                const alias = references.toAlias(value, key);
                let normalizedName;
                
                if (["reason_code", "mcc"].includes(key) && alias !== value) {
                    normalizedName = `<div class="multiline-content">
                        <div class="line-1">${value}</div>
                        <div class="line-2">${alias}</div>
                    </div>`;
                } else if (["currency_alias", "purchase_currency"].includes(key)) {
                    // Do not translate currency
                    normalizedName = value;
                } else if (key === "date_time") {
                    const [line1, line2] = alias.split("<br/>");
                    if (line2) {
                        normalizedName = `<div class="multiline-content">
                                              <div class="line-1">${line1}</div>
                                              <div class="line-2">${line2}</div>
                                          </div>`;
                    } else {
                        normalizedName = line1;
                    }
                } else {
                    normalizedName = alias;
                }
                
                if (obj.$count || obj.filler) {
                    return common.treetable(obj, common, value, c) + normalizedName;
                }

                let baseDateRangeIcon;

                // places an icon in the first cell of a pivot table row when the values for `baseDateRangeIcon` and `normalizedName` match
                if (["date_time", "date_range"].some((pivot) => rows.includes(pivot)) && !!this.config.dates) {
                    const [baseRangeString] = this.config.dates;
                    
                    if (baseRangeString === normalizedName) {
                        baseDateRangeIcon = "<i class='pivot-base-range far fa-map-marker-alt' title='Base date range'></i>";
                    }
                }
                
                return [
                    common.treetable(obj, common, value, c),
                    baseDateRangeIcon,
                    normalizedName
                ].filter(Boolean).join(" ");
            };
            
            // Convert all references in columns to aliases
            if (Array.isArray(columns) && columns.length) {
                config.header.forEach((header, index) => {
                    // Skip checkbox column and rows column
                    if (index === 0 || ((checkbox || recon3actions) && index === 1)) return;
                    
                    for (let i = 0; i < columns.length; i++) {
                        // Custom header management for date type tooltip: render a container and inject react tooltip after render
                        const getter = getters.find(({ key, valueType }) => (key === columns[i] && valueType === "date_time"));
                        if (getter) {
                            header.header[i].text = ReactDOMServer.renderToStaticMarkup(
                                <div className="webix-header-tooltip" data-title={this.config.fieldMap[getter.key]} data-text={references.toAlias(header.header[i].text, columns[i])}>{references.toAlias(header.header[i].text, columns[i])}</div>
                            );
                        } else {
                            header.header[i].text = references.toAlias(header.header[i].text, columns[i]);
                        }
                    }
                });
            }
            
            if (Array.isArray(config.header)) {
                config.header = config.header.map(columnMapIteratee);
            }
            
            // Resize first column to fill space if table has horizontal scroll
            const parentWidthNormalized = parentWidth || this.$view.clientWidth;
            if (config.header && config.header.length) {
                const currentWidth = config.header.reduce((memo, { hidden, width }) => {
                    if (hidden) return memo;
                    return memo + width;
                }, 0);
                if (currentWidth < parentWidthNormalized) {
                    config.header[checkbox || recon3actions ? 1 : 0].fillspace = true;
                } else {
                    config.header[checkbox || recon3actions ? 1 : 0].adjust = true;
                }
            }
        };
    },
    
    onPopup({
        fieldMap, onChangeFields, onApplyPopup, isCustom, pazienPivot
    }) {
        return function(popup) {
            const parentPivotTable = webix.$$(popup.config.pivot);
            // Store current structure
            let prevStructure;

            popup.$$("fields").attachEvent("onAfterLoad", function() {
                prevStructure = parentPivotTable.getStructure();
                this.filter((item) => Object.prototype.hasOwnProperty.call(fieldMap, item.name));
                this.data.sort("text");
            });
            
            let ackedMissingCurrencyPivot = false;
            
            const onFieldsChanged = ({ name = "", text = "" }) => {
                const structure = popup.getStructure();
                const pivotFields = new Set([
                    ...structure.columns,
                    ...structure.filters.map((v) => v.name),
                    ...structure.rows
                ]);
                const values = [...structure.values, { name, text }];

                // Handle "refunds_purchase_amount", "sales_purchase_amount" fields
                // We should not present these fields without a currency field/pivot
                // Options are: normalized amount field or amount field + currency field/pivot
                const originalCurrencyField = values.find(({ name: fieldName }) => startsWith(fieldName, "original_"));
                const purchaseCurrencyField = values.find(({ name: fieldName }) => ["refunds_purchase_amount", "sales_purchase_amount"].includes(fieldName));
                
                let buttons = {};
                let showPopup = false;
                if (!ackedMissingCurrencyPivot && (originalCurrencyField && !pivotFields.has("currency_alias"))) {
                    // Based on analytics section we have different currency types.
                    const currency = popup.$$("fields").find({ name: "currency_alias" });
                    buttons = {
                        currency: {
                            text: `Add ${currency[0].text.toLowerCase()}`,
                            className: "btn-confirm"
                        },
                        cancel: {
                            visible: true
                        }
                    };
                    showPopup = true;
                } else if (!ackedMissingCurrencyPivot && (purchaseCurrencyField && !pivotFields.has("purchase_currency"))) {
                    buttons = {
                        purchase: {
                            text: "Add purchase currency",
                            className: "btn-confirm"
                        },
                        cancel: {
                            visible: true
                        }
                    };
                    showPopup = true;
                }
                
                if (showPopup) {
                    sweetalert({
                        className: "wide-alert",
                        text: `${(originalCurrencyField || purchaseCurrencyField).text || "Selected field"} requires a currency type in order to calculate`,
                        buttons
                    }).then((acked) => {
                        if (acked) {
                            const p = popup;
                            let field;
                            if (acked === "currency") {
                                const match = p.$$("fields").find({ name: "currency_alias" });
                                if (match) {
                                    field = match[0];
                                }
                            } else if (acked === "purchase") {
                                const match = p.$$("fields").find({ name: "purchase_currency" });
                                if (match) {
                                    field = match[0];
                                }
                            }
                            
                            if (field) {
                                p._dropField({
                                    from: p.$$("fields"),
                                    to: p.$$("rows"),
                                    start: [field.id]
                                });
                            } else {
                                captureException(new Error("Cannot find currency field in pivot configurator"));
                            }
                        } else {
                            ackedMissingCurrencyPivot = true;
                        }

                        return true;
                    });
                }

                // Handle value drop in change over time table
                // Inject change column on associated to dropped value
                if (popup.config.id === "changeOverTime") {
                    const viewChangeAs = popup.config.viewChangeAs;
                    if (name.includes("_change_")) return; // return if not an original value
                    popup.$$("values").add({
                        text: `${text} - change (${viewChangeAs === "abs" ? "Abs" : "%"})`,
                        name: `${viewChangeAs}_change_${name}`,
                        operation: viewChangeAs === "abs" ? "sum" : "weighted_sum"
                    });
                }
            };
            
            // Handle drop from "Value options" to "Values"
            popup.$$("values").attachEvent("onBeforeDrop", function(objectContext, event) {
                if (Array.isArray(objectContext.source) && objectContext.source[0]) {
                    const matchingField = this.getItem(objectContext.source[0]);
                    if (matchingField) {
                        onFieldsChanged(matchingField);
                    }
                }
            });
            popup.$$("rows").attachEvent("onBeforeDrop", function(objectContext, event) {
                if (!isCustom && Array.isArray(objectContext.source) && objectContext.source[0]) {
                    const sourceRow = this.getItem(objectContext?.source[0]);
                    const targetRow = this.getItem(objectContext.target);
                    if (sourceRow?.name === pazienPivot || targetRow?.name === pazienPivot) {
                        sweetalert({
                            className: "wide-alert",
                            icon: "warning",
                            content: makeElFromString({
                                content: "Create a custom card to remove a top-level pivot.<br/><br/>"
                                        + "(The “Duplicate” button will create a custom version of this exact card.)"
                            }),
                            buttons: {
                                cancel: {
                                    visible: true,
                                    text: "OK"
                                }
                            }
                        });
                        return false;
                    }
                }
            });
            popup.$$("rows").attachEvent("onBeforeDelete", function(objectContext, event) {
                if (!isCustom && objectContext) {
                    const sourceRow = this.getItem(objectContext);
                    if (sourceRow?.name === pazienPivot) {
                        sweetalert({
                            className: "wide-alert",
                            icon: "warning",
                            content: makeElFromString({
                                content: "Create a custom card to remove a top-level pivot.<br/><br/>"
                                    + "(The “Duplicate” button will create a custom version of this exact card.)"
                            }),
                            buttons: {
                                cancel: {
                                    visible: true,
                                    text: "OK"
                                }
                            }
                        });
                        return false;
                    }
                }
            });
            // Change over time table popup tweak.
            // Delete associated change field when deleting the original one
            popup.$$("values").attachEvent("onBeforeDelete", (id) => {
                const item = popup.$$("values").getItem(id);
                const objList = popup.$$("values").find((obj) => obj.name.includes(`_change_${item.name}`) && !startsWith(obj.name, "internal_change_"));
                if (objList) {
                    popup.$$("values").remove(objList.map(({ id: valueId }) => valueId));
                }
            });
            
            const handleSameFieldInValues = function(item, list) {
                // Find last item in the values list
                const match = popup.$$(list).find({ name: item.name });
                if (match[0].operation.length > 3) {
                    sweetalert({
                        className: "wide-alert",
                        icon: "warning",
                        content: makeElFromString({
                            content: "Only five columns of the same value are allowed"
                        }),
                        buttons: {
                            cancel: {
                                visible: true,
                                text: "OK"
                            }
                        }
                    }).then(() => {
                        popup._removeListField(list, match[0]);
                    });
                } else if (match[0].operation.length > 1 && match[0].operation[0] === last(match[0].operation)) {
                    // If the operation is the same as the original operation then use reserved sum operation
                    match[0].operation[match[0].operation.length - 1] = `sum${match[0].operation.length}`;
                }
            };
            
            const onIncorrectValueDropped = function(template, item, list) {
                const suggestedList = list === "values" ? "rows" : "values";
                const buttons = {
                    cancel: {
                        visible: true
                    },
                    add: {
                        text: `Add to ${suggestedList}`,
                        className: "btn-confirm"
                    }
                };
                
                if (list === "values") {
                    // Don't show add button when dropped a pivot-type into values
                    delete buttons.add;
                    buttons.cancel.text = "OK";
                }
                
                let warningContent;
                if (template === "pivot_table_value_warning") {
                    warningContent = `<b>${item.text}</b> is a pivot field and can only be added to Rows, Filters, or Columns box`;
                } else if (template === "pivot_table_pivot_warning") {
                    warningContent = `<b>${item.text}</b> is a calculated value and can only be added to the <b>Σ Values box</b>`;
                }
                
                sweetalert({
                    className: "wide-alert",
                    icon: "warning",
                    content: makeElFromString({
                        content: warningContent
                    }),
                    buttons
                }).then((acked) => {
                    // Remove the item from its current list
                    const match = popup.$$(list).find({ name: item.name }, true);
                    if (!match) {
                        captureException(new Error(`Cannot find item ${item.name} in when deleting from view ${list}`));
                        return;
                    }
                    
                    // Remove the field first
                    popup._removeListField(list, match);
                    
                    if (acked) {
                        // Handle the case when moving from Values box.
                        // Webix keeps a copy of item there when you drag a field first time (WTF), so we need to make sure not to create a duplicate
                        if (popup.$$(suggestedList).find({ name: item.name }).length) {
                            return;
                        }
                        
                        const matchedInFields = popup.$$("fields").find({ name: item.name }, true);
                        if (!matchedInFields) {
                            captureException(new Error(`Cannot find item ${item.name} in when moving from Fields to view ${suggestedList}`));
                            return;
                        }
                        
                        // Then find it in fields list and move to suggested list if ACKed
                        popup._dropField({
                            from: popup.$$("fields"),
                            to: popup.$$(suggestedList),
                            start: [matchedInFields.id]
                        });
                    }
                });
            };
            
            // It's easy to detect drops when item is coming from Fields box; hovewer when moving items between the boxes, the `from` list doesn't contain
            // the item by the time onBeforeDrop event is called. Therefore, we store interactiveItem in a variable on onBeforeDrag event.
            // This way the item always contain `$unitValue` prop used later in onBeforeDrop handler.
            let interactiveItem;
            ["fields", "columns", "filters", "rows", "values"].forEach((view) => {
                popup.$$(view).attachEvent("onBeforeDrag", ({ start, from, to }) => {
                    const { name } = from.getItem(start);
                    interactiveItem = popup.$$("fields").find({ name }, true);
                });
            });
            
            // Bind event on drops in pivot-type boxes
            ["columns", "filters", "rows", "values"].forEach((view) => {
                popup.$$(view).attachEvent("onBeforeDrop", ({ start, from, to }) => {
                    if (interactiveItem.$unitValue === strings.pivot_table_rows_columns_filters && view === "values") {
                        onIncorrectValueDropped("pivot_table_value_warning", interactiveItem, view);
                    } else if (interactiveItem.$unitValue === strings.pivot_table_values && ["columns", "filters", "rows"].includes(view)) {
                        onIncorrectValueDropped("pivot_table_pivot_warning", interactiveItem, view);
                    }
                    
                    if (view === "values") {
                        handleSameFieldInValues(interactiveItem, view);
                    }
                });
            });
            
            const hidePopupOnEscKey = function(event) {
                let isEscape = false;
                if ("key" in event) {
                    isEscape = event.key === "Escape" || event.key === "Esc";
                } else {
                    isEscape = event.keyCode === 27;
                }
                
                if (isEscape) {
                    popup.hide();
                }
            };
            
            popup.attachEvent("onShow", () => {
                document.addEventListener("keydown", hidePopupOnEscKey);
            });
            popup.attachEvent("onHide", () => {
                document.removeEventListener("keydown", hidePopupOnEscKey);
            });
            
            popup.attachEvent("onApply", (newStructure) => {
                // Check if we're applying new structure to the pivot table by comparing previous structure elements to the new structure
                const flattenPrev = [
                    ...prevStructure.columns,
                    ...prevStructure.filters.map((v) => v.name),
                    ...prevStructure.rows
                ];
                const flattenNew = [
                    ...newStructure.columns,
                    ...newStructure.filters.map((v) => v.name),
                    ...newStructure.rows
                ];
                
                const diff = difference(flattenNew, flattenPrev);
                
                const obj = {
                    diff,
                    // Create another new structure object to avoid passing mutated `structure.columnSort`
                    newStructure: {
                        columns: newStructure.columns,
                        filters: newStructure.filters,
                        rows: newStructure.rows,
                        values: newStructure.values
                    },
                    prevStructure: {
                        columns: prevStructure.columns,
                        filters: prevStructure.filters,
                        rows: prevStructure.rows,
                        values: prevStructure.values
                    },
                    fields: flattenNew
                };
                if (diff.length) {
                    onChangeFields(obj);
                } else {
                    setTimeout(() => {
                        onApplyPopup(obj);
                    }, 0);
                }
    
                emitter.emit("tracking.track", {
                    type: "pivot_table.apply",
                    data: {
                        newStructure: JSON.stringify(newStructure),
                        prevStructure: JSON.stringify(prevStructure),
                        diff: JSON.stringify(diff)
                    }
                });
            });
        };
    },
    
    onAfterLoad({ before = () => {}, fieldFormat, initialSorting = {} } = {}) {
        return function() {
            before();
            
            // Remove not-supported filters
            // https://docs.webix.com/pivot__filters.html#operationswithfilters
            this.filters.remove("datepicker");
            this.filters.remove("text");
            
            // Remove not-supported operations
            delete this.operations.count;
            delete this.operations.min;
            delete this.operations.max;
            
            // Check if there is initialSorting defined in the model
            let { field } = initialSorting;
            const { dir } = initialSorting;
            
            if (!field || !dir) {
                return;
            }
            
            const { rows, values } = this.getStructure();
            
            // If provided sort field is not found then use first available value
            if (!values.some(({ name }) => name === field)) {
                field = values[0].name;
            }
            
            if (rows.includes("date_range")) {
                const index = rows.indexOf("date_range") + 1;
    
                // Sort date ranges based on their index in the config
                this.$$("data").sort((a, b) => {
                    if (a.$level !== index && b.$level !== index) {
                        return a[field] > b[field] ? -1 : 1;
                    }
                    
                    return this.config.dates.indexOf(a.name) - this.config.dates.indexOf(b.name);
                });
            } else {
                const as = {
                    number: "int",
                    amount: "int",
                    rate: "int",
                    "default": "string"
                };
                // Find matching field in the pivot fields and determine its type
                const formatsMatch = fieldFormat.find((f) => f.name === field);
    
                const sortAs = as[formatsMatch?.valueType || "default"];
                this.$$("data").sort(`sum_'_${field}`, dir, sortAs);
            }
        };
    },
    
    popup_onViewInit({ fieldMap, pivots = {} } = {}) {
        return function(name, config) {
            const popup = this.getTopParentView();
            if (name === "configTitle") {
                config.template = (obj) => ReactDOMServer.renderToStaticMarkup(
                    <div id="pivot_table_config_header">
                        <div>Pivot table options</div>
                        <div>Drag options below to boxes at right to add fields to table</div>
                    </div>
                );
            }

            if (name === "body") {
                try {
                    const [filtersLayout, columnsLayout] = config.rows[0].cols[1].rows[0].rows[0].cols;
                    const [rowsLayout, valuesLayout] = config.rows[0].cols[1].rows[0].rows[1].cols;
                    
                    config.rows[0].cols[1].rows = [
                        { type: "wide", cols: [rowsLayout, columnsLayout] },
                        { type: "wide", cols: [valuesLayout, filtersLayout] }
                    ];
                } catch (error) {
                    console.warn("Unable to sort pivot configuration dialog widgets", error);
                }
            }

            if (name === "fieldsLayout") {
                // Add search field to the fields layout
                config.rows.splice(1, 0, {
                    view: "search",
                    placeholder: "Search pivot table options",
                    on: {
                        onTimedKeyPress() {
                            const list = popup.$$("fields");
                            const value = this.getValue().toLowerCase();
                            list.filter("name", (item) => item.toLowerCase().includes(value) && Object.prototype.hasOwnProperty.call(fieldMap, item));
                        }
                    }
                });
            } else if (name === "fields") {
                // Trigger the event to allow the parent to group fields
                // Change view type to unit list to group items
                config.view = "orderedunitlist";
                
                // Add groupBy function
                config.uniteBy = function(item) {
                    if (Object.prototype.hasOwnProperty.call(pivots, item.name)) {
                        return strings.pivot_table_rows_columns_filters;
                    }
                    return strings.pivot_table_values;
                };
                
                // Sort headers
                config.unitsOrder = [strings.pivot_table_rows_columns_filters, strings.pivot_table_values];
            } else if (["filtersHeader", "valuesHeader", "rowsHeader", "columnsHeader"].includes(name)) {
                // Update icons
                config.template = (obj) => {
                    let icon;
                    if (obj.icon) {
                        if (obj.icon === "values") {
                            icon = "<span class='header-icon'><b>Σ</b></span>";
                        } else {
                            icon = `<span class="header-icon fal fa-${obj.icon}"></span>`;
                        }
                    } else {
                        icon = `<span class='header-icon'>${obj.iconContent}</span>`;
                    }
                    
                    return `${icon}<span class="header-value">${webix.i18n.pivot[obj.value]}</span><span class="webix_clear_list">Clear</span>`;
                };
                
                // Extend header view config with event handler on click coming from our injected span.webix_clear_list
                config.onClick = Object.assign(config.onClick || {}, {
                    webix_clear_list() {
                        // Get a list view instance
                        const list = popup.$$(this.data.value);
                        
                        // Now iterate over all items in the list and call _removeListField on each item
                        list.data.each((item) => {
                            // Not sure why we can't synchronously remove all items,
                            // so calling each removal with nextTick method
                            // Also, _removeListField is a method on the popup, not a list (weird)
                            nextTick(() => popup._removeListField(this.data.value, item));
                        });
                    }
                });
            }
        };
    }
};

export const datatableEvents = {
    onHeaderClick({ checkbox = false, recon3actions = false } = {}) {
        return function({ column }, event) {
            const pivot = this.getTopParentView();
            const index = checkbox || recon3actions ? 1 : 0;
            if (this.getColumnIndex(column) === index) { // opens the modal for "Pivot table options" when a user clicks on the first column after the column which renders checkboxes
                if (pivot.config.readonly) {
                    // We should differentiate between regular "readonly" pivot table and those where table are readonly because of feature_tables_multi_pivots permission
                    if (!store.getState().permissions.user_permissions.feature_tables_multi_pivots) {
                        Radio.channel("main").request("disabled:permission", "feature_tables_multi_pivots");
                    }
                } else {
                    pivot.configure();
                }
            }
        };
    },
    
    onCheck({ callback = () => {} } = {}) {
        return function(row, column, state) {
            callback(getSelectedRows({ datatable: this, row, state }));
        };
    },
    
    onItemClick({ checkbox = false, callback = () => {} } = {}) {
        return function({ row, column }) {
            if (checkbox && column === "ch1") {
                const item = this.getItem(row);
                const { checkValue, uncheckValue } = this.getColumnConfig(column);
                
                const state = item[column] === checkValue ? uncheckValue : checkValue;
                
                this.updateItem(row, {
                    [column]: state
                }, "update");
                
                callback(getSelectedRows({ datatable: this, row, state }));
            }
        };
    },
    
    onAfterLoad({ chart = false } = {}) {
        return function() {
            this.hideOverlay();
            if (!this.count()) {
                this.config.columns.forEach((col) => {
                    if (col.id !== "control") this.adjustColumn(col.id, "header");
                });
                this.showOverlay(
                    <ErrorView
                        header="No data to display."
                        message="We couldn't find any records for your request"
                        captureInSentry={false}
                        custom
                        hideDetails
                        hideConfig
                        actions={false}
                    />
                );
            }
        };
    },
    
    onAfterRender() {
        return function() {
            // Injecting react tooltips
            const tooltips = Array.from(document.getElementsByClassName("webix-header-tooltip"));
            tooltips.forEach((container) => render(<>{container.dataset.text} <Tooltip title={container.dataset.title || ""}><FontAwesomeIcon icon={faQuestionCircle} /></Tooltip></>, container));
        };
    }
};

const getSelectedRows = function({ datatable, row, state }) {
    const pivot = datatable.getTopParentView();
    pivot.selectedRows[row] = state;
    
    return Object.entries(pivot.selectedRows)
        .filter(([r, s]) => s === "on")
        .map(([r]) => getItemFiltersById({ id: r, pivot }));
};

export const getItemFiltersById = function({ id, pivot }) {
    const data = pivot.$$("data");
    // Create an array that will hold all levels of data, starting with row that was clicked
    const allItems = [data.getItem(id)];
    while (allItems[allItems.length - 1].$parent) {
        // While there is a parent, keep adding items to an array,
        // so first element is lowest level row that triggered the event, and the last element is the top level
        allItems.push(data.getItem(allItems[allItems.length - 1].$parent));
    }
    
    // Hold a reference to structure.rows
    const { rows } = pivot.getStructure();
    
    // Build an array of tuples to include [ref_type, ref_name]
    // Go thru each found item
    return allItems.map((item) => {
        // Match item level to structure.rows index to find item type
        const type = rows[item.$level - 1];
        
        // Alias is a `name` in webix treedata item
        const alias = item.name;
        
        // Now go thru entire data collection to find item that has a `type` equal to `alias`(name)
        // It does contain it because of a transformation in pivotTable.js:29 in `pivotTablePrepareData` method
        
        const dataItem = Object.values(pivot.data.pull).find((d) => {
            let extraAlias;
            if (alias === "true") {
                extraAlias = true;
            } else if (alias === "false") {
                extraAlias = false;
            } else if (Number.isFinite(Number.parseInt(alias, 10))) {
                extraAlias = Number.parseInt(alias, 10);
            }
            
            return d[type] === alias || (typeof extraAlias !== "undefined" && d[type] === extraAlias);
        });
        // There was an error thrown for two users already because dataItem was not found in the collection
        // https://sentry.io/pazien/pazien-web-application/issues/571453714/?referrer=slack
        
        if (typeof dataItem === "undefined") {
            captureMessage("Cannot drilldown because underlying item is not found", {
                level: "warning",
                extra: {
                    alias,
                    type,
                    collection: pivot.data.pull
                }
            });
            return [type, ""];
        }
        
        return [type, dataItem[type]];
    });
};

// Webix mutates "filters" object in structure with some function as a property.
// We cannot serialize such objects. Pick only { name, text, type, fvalue }
export const makeSerializablePivotStructure = function({
    values = [],
    rows = [],
    columns = [],
    filters = []
}) {
    return {
        values: values.map((r) => pick(r, ["name", "text", "operation"])),
        rows: [...rows],
        columns: [...columns],
        filters: filters.map(({ name, text, type, fvalue }) => ({
            name, text, type, fvalue
        }))
    };
};

export const calculateFlaggedReconciled = (datatable, obj) => {
    let sources = [];
    // datatable has only summary data, we need a top level pivot to access raw data
    const pivot = datatable.getTopParentView();
    if (datatable.data.isBranch(obj.id)) {
        datatable.data.eachLeaf(obj.id, (leaf) => {
            const leafSource = leaf.$source || [];
            sources = [...sources, ...leafSource];
        });
    } else {
        sources = [...obj?.$source || []];
    }
    
    return sources.reduce((acc, id) => {
        const current = pivot.data.getItem(id);
        return {
            reconciled: acc.reconciled && current.reconciled,
            flagged: acc.flagged && current.flagged
        };
    }, {
        reconciled: true,
        flagged: true
    });
};

export const setObjectParam = (datatable, obj, params) => {
    Object.assign(obj, params);
    if (datatable.data.isBranch(obj.id)) {
        datatable.data.eachSubItem(obj.id, (leaf) => {
            Object.assign(leaf, params);
        });
    } else {
        // Need to alter raw data, as it is used in calculateFlaggedReconciled
        const pivot = datatable.getTopParentView();
        obj.$source.forEach((srcId) => {
            const raw_obj = pivot.data.getItem(srcId);
            Object.assign(raw_obj, params);
        });
    }

    if (obj.$parent) {
        const parent = datatable.getItem(obj.$parent);
        const parentParams = calculateFlaggedReconciled(datatable, parent);
        setParentParams(datatable, parent, parentParams);
    }

    datatable.render();
};

// traverse to root of the tree, recalculate and set params
export const setParentParams = (datatable, obj, objParams) => {
    Object.assign(obj, objParams);
    if (obj.$parent) {
        const parent = datatable.getItem(obj.$parent);
        const parentParams = calculateFlaggedReconciled(datatable, parent);
        setParentParams(datatable, parent, parentParams);
    }
};
